import { useEffect, useState } from "react";
import { Star, Quote } from "lucide-react";
import { REVIEWS } from "@/content/reviews";
import { Reveal } from "@/hooks/useReveal";

const FEATURED = REVIEWS.filter((r) => r.featured);

/** Long-form rotating spotlight review with portrait, rating, role. */
export const TestimonialSpotlight = () => {
  const [idx, setIdx] = useState(() =>
    Math.floor(Math.random() * FEATURED.length),
  );

  useEffect(() => {
    const t = window.setInterval(
      () => setIdx((i) => (i + 1) % FEATURED.length),
      9000,
    );
    return () => window.clearInterval(t);
  }, []);

  const r = FEATURED[idx];
  if (!r) return null;

  return (
    <section data-visual-dynamic="testimonial-spotlight" className="py-20 md:py-28 border-t border-border bg-navy-deep relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,hsl(var(--gold)/0.06),transparent_60%)]"
      />
      <div className="container relative max-w-4xl">
        <Reveal variant="up">
          <div className="text-center mb-10">
            <span className="eyebrow !mx-auto">Client Spotlight</span>
            <h2 className="font-display font-bold uppercase text-white text-3xl md:text-5xl mt-5 leading-[1.05]">
              Real leaders.<br />
              <span className="text-gradient-gold">Real results.</span>
            </h2>
          </div>
        </Reveal>

        <Reveal key={r.name} variant="fade">
          <figure className="relative border border-gold/30 bg-gradient-to-br from-gold/[0.05] to-navy p-8 md:p-12">
            <Quote
              className="absolute top-6 left-6 h-10 w-10 text-gold/20"
              aria-hidden
            />

            <div className="flex flex-col md:flex-row items-start gap-6 md:gap-10">
              <img
                src={r.image}
                alt={`Portrait of ${r.name}`}
                className="h-24 w-24 md:h-32 md:w-32 rounded-full object-cover border-2 border-gold/50 flex-shrink-0 shadow-[0_10px_30px_-10px_hsl(var(--gold)/0.4)]"
              />

              <div className="flex-1">
                <div className="flex gap-1" role="img" aria-label="5 out of 5 stars">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} className="h-4 w-4 fill-gold text-gold" />
                  ))}
                </div>

                <blockquote className="mt-5 text-white text-lg md:text-2xl font-light leading-relaxed italic">
                  "{r.quote}"
                </blockquote>

                <figcaption className="mt-6 pt-5 border-t border-gold/20">
                  <div className="font-display uppercase text-white text-base tracking-wide">
                    {r.name}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-gold mt-1">
                    {r.role}
                  </div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-silver/70 mt-1">
                    {r.location}
                  </div>
                </figcaption>
              </div>
            </div>

            {/* Indicator dots */}
            <div className="mt-8 flex justify-center gap-2">
              {FEATURED.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => setIdx(i)}
                  aria-label={`Show testimonial ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === idx ? "w-8 bg-gold" : "w-1.5 bg-silver/30"
                  }`}
                />
              ))}
            </div>
          </figure>
        </Reveal>
      </div>
    </section>
  );
};
